import React, { useContext } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
// HOMEPAGE
import NewHomeDashboardPage from './NewHomeDashboardPage';

// INDUSTRY UPDATES
import IndustryUpdates from './industry/IndustryUpdates';

// HELP CENTER
import FAQ from './training/FAQ';

// DATA MODULES
import DataPage from './data/DataPage';
import Watchlist from 'pages/watchlist/Watchlist';
import BiteList from 'pages/bite-list/BiteList';
import TradeProtection from 'pages/trade-protection/TradeProtection';
import CyberProtection from 'pages/cyber-protection/CyberProtection';
import BiteScore from 'pages/bite-score/BiteScore';
import BiteBulk from 'pages/bite-bulk/BiteBulk';

// SCREENING MODULES
import Classification from 'pages/classify/Classification';
import TariffPage from './tariffs/TariffPage';
import BiteAIStream from 'pages/ask-tool/BiteAIStream';
import BiteViz from 'pages/bite-viz/BiteViz';
import EntityScreening from './screening/Entities';
import AlertsScreening from './screening/Alerts';
import ImportScreening from './screening/ImportScreeningComposer';
import ExportScreening from './screening/ExportScreeningComposer';

// Monitor
import Case from 'pages/cases/Case';
import VaultHome from './cases/VaultHome';
import Monitor from './monitor/Monitor';
import Ticket from './monitor/Ticket';

// ACCOUNT
import JoinTeamFromInvite from 'pages/teams/JoinTeamFromInvite';
import SuperAdminDashboard from './superAdmin/SuperAdminDashboard';
import TeamAccess from 'pages/superAdmin/TeamAccess';
import LeadershipOverview from './leadership/LeadershipOverview';
import ManualTeamSetup from 'pages/superAdmin/ManualTeamSetup';
import TeamSelectView from 'pages/superAdmin/TeamSelectView';
import ProfileHome from 'pages/profile/ProfileHome';
import AdminCreateUser from 'components/SuperAdmin/AdminCreateUser';

// TRAINING VIDEOS
import TrainingVideos from 'components/Training/videos/TrainingVideos';
import LicensesVideo from 'components/Training/videos/LicensesVideo';
import ModuleVideo from 'components/Training/videos/ModuleVideo';
import PaymentsVideo from 'components/Training/videos/PaymentsVideo';
import QuickStartVideo from 'components/Training/videos/QuickStartVideo';
import VaultVideo from 'components/Training/videos/VaultVideo';
import ScreenVideo from 'components/Training/videos/ScreenVideo';
import ModulesVideo from 'components/Training/videos/ModulesVideo';
import BulkVideo from 'components/Training/videos/BulkVideo';
import GPTVideo from 'components/Training/videos/GPTVideo';
import BITEUVideo from 'components/Training/videos/BiteUVideo';

// BITE DB
import BiteDB from './bite-db/BiteDB';
import Products from './bite-db/ProductDB';
import EntityDB from './bite-db/EntityDB';
import Supplier from './bite-db/Supplier';
import EntityVerificationReport from './bite-db/EntityVerificationReport';
import Product from './bite-db/Product';
import EditProduct from '../components/BiteDB/Modal/EditProduct';
import Tariff from './bite-db/Tariff';
import Database from './bite-db/Database';
import ScreeningPage from './bite-db/Screening';
import ReviewQ from './bite-db/ReviewQ';
import StatsRoll from './bite-db/StatsRoll';
import ECCNClassification from './classify/ECCNClassification';
import EARClassification from './classify/EARClassification';
import { GlobalContext } from 'providers/GlobalProvider';
import ImportPage from './bite-import/importPage';
import ExportPage from './bite-export/ExportPage';
import ExportDesignPage from './bite-export-design/ExportDesignPage';
import BeneficialOwnershipPage from './bite-beneficial-ownership/BeneficialOwnershipPage';
import AceAnalyzerPage from './analyzers/AceAnalyzers';
import TariffAnalyzerPage from './analyzers/TariffAnalyzers';
import CustomsEntryAnalyzer from './analyzers/CustomsEntryAnalyzer';
import AceRunDetailsPage from './analyzers/AceRunDetailsPage';
import TariffRunDetailsPage from './analyzers/TariffRunDetailsPage';
import AesAnalyzerPage from './analyzers/AesAnalyzers';
import AesRunDetailsPage from './analyzers/AesRunDetailsPage';

// EXPORT HUB
import ExportHub from './export-hub/ExportHub';

// IMPORT HUB
import ImportHub from './import/ImportHub';

// REPORTS
import AIReportBuilder from './reports/AIReportBuilder';

// Tenant
import NoTenantAccess from './tenant/NoTenantAccess';
import {
  isTeamOnboardingRoute,
  requiresTeamOnboarding,
} from 'utils/teamOnboarding';

const Routes: React.FC = () => {
  const location = useLocation();
  // One unconditional read: the whitelabel branch below returns before the
  // non-whitelabel path is reached, so pulling these from separate useContext
  // calls inside/after that branch made the hook order depend on isWhiteLabel.
  const { isWhiteLabel, tenant_access, sessionInvalidated, cur_user } =
    useContext(GlobalContext);

  if (isWhiteLabel) {
    if (
      sessionInvalidated ||
      !tenant_access ||
      !tenant_access.domain ||
      !tenant_access.tenantAccess
    ) {
      return (
        <Switch>
          <Route exact path='/no-tenant-access' component={NoTenantAccess} />
          <Redirect to='/no-tenant-access' />
        </Switch>
      );
    }

    const hasTariffAnalyzerAccess = tenant_access.tenantAccess.some(
      (access) => access.name === 'Tariff Analyzer',
    );

    const hasTariffAccess = tenant_access.tenantAccess.some(
      (access) => access.name === 'Tariff Checker',
    );

    const hasExportAccess = tenant_access.tenantAccess.some(
      (access) => access.name === 'Export Tool',
    );

    const hasImportAccess = tenant_access.tenantAccess.some(
      (access) => access.name === 'Import Tool',
    );

    const hasClassifyAccess = tenant_access.tenantAccess.some(
      (access) => access.name === 'Classify Tool',
    );

    const hasEntityDatabaseAccess = tenant_access.tenantAccess.some(
      (access) => access.name === 'Entity Database' || access.name === 'Entity Hub',
    );

    const hasEntityAccess = tenant_access.tenantAccess.some(
      (access) => access.name === 'Entity Tool',
    );

    const hasECCNAccess = tenant_access.tenantAccess.some(
      (access) => access.name === 'ECCN Tool',
    );
    return (
      <Switch>
        {/* Tariff */}

        {/* Direct Switch children (no fragment), most specific first, so only
            one of list/details renders. */}
        {hasTariffAnalyzerAccess && (
          <Route path='/tariff-analyzers/:runId' component={TariffRunDetailsPage} />
        )}
        {hasTariffAnalyzerAccess && (
          <Route path='/tariff-analyzers' component={TariffAnalyzerPage} />
        )}

        {(hasTariffAccess || hasTariffAnalyzerAccess) && (
          <Route path='/tariff-checker' component={TariffPage} />
        )}

        {hasExportAccess && (
          <Route path='/export-screening' component={ExportScreening} />
        )}

        {hasImportAccess && (
          <Route path='/import-screening' component={ImportScreening} />
        )}

        {hasClassifyAccess && (
          <Route path='/classify' component={Classification} />
        )}

        {hasEntityAccess && (
          <Route path='/entity-screening' component={EntityScreening} />
        )}

        {hasECCNAccess && <Route path='/eccn' component={ECCNClassification} />}

        {/* Each Route must be a DIRECT child of the Switch. A fragment wrapper
            has no `path`, and v5's Switch treats a pathless child as an
            unconditional match — it would stop here for every unmatched URL
            (including '/'), render neither nested Route, and never reach the
            redirects below. */}
        {hasEntityDatabaseAccess && (
          <Route path='/database/:id' component={Database} />
        )}
        {hasEntityDatabaseAccess && (
          <Route
            path='/supplier/:id/verification'
            component={EntityVerificationReport}
          />
        )}
        {hasEntityDatabaseAccess && (
          <Route path='/supplier/:id' component={Supplier} />
        )}

        {hasTariffAnalyzerAccess && <Redirect to='/tariff-analyzers' />}
        {hasTariffAccess && <Redirect to='/tariff-checker' />}
        {hasEntityAccess && <Redirect to='/entity-screening' />}
        {hasClassifyAccess && <Redirect to='/classify' />}
        {hasImportAccess && <Redirect to='/import-screening' />}
        {hasExportAccess && <Redirect to='/export-screening' />}
        {hasECCNAccess && <Redirect to='/eccn' />}
        
        <Route exact path='/no-tenant-access' component={NoTenantAccess} />
        <Redirect to='/no-tenant-access' />
      </Switch>
    );
  }
  const isQuickcodeOnly = Boolean(cur_user?.quickcodeOnly);
  const isDomainRestricted = cur_user?.role === 'domain-restricted';

  if (
    requiresTeamOnboarding(cur_user) &&
    !isTeamOnboardingRoute(location.pathname)
  ) {
    return <Redirect to='/' />;
  }

  if (isQuickcodeOnly) {
    return (
      <Switch>
        <Route exact path='/' render={() => <Redirect to='/bite-db' />} />
        <Route exact path='/bite-db/products' component={Products} />
        <Route exact path='/bite-db' component={BiteDB} />
        <Route
          path='/supplier/:id/verification'
          component={EntityVerificationReport}
        />
        <Route path='/supplier/:id' component={Supplier} />
        <Route path='/product/:id' component={Product} />
        <Route path='/tariff/:id' component={Tariff} />
        <Route path='/database/:id' component={Database} />
        <Route path='/review-queue' component={ReviewQ} />
        <Route path='/stats-roll-up' component={StatsRoll} />
        <Route path='/screening-setting' component={ScreeningPage} />
        <Redirect to='/bite-db' />
      </Switch>
    );
  }

  if (isDomainRestricted) {
    return (
      <Switch>
        <Route
          exact
          path='/'
          render={() => <Redirect to='/entity-screening' />}
        />
        <Route path='/entity-screening' component={EntityScreening} />
        <Route path='/bite-monitor' component={Monitor} />
        <Route path='/monitor/ticket/:ticket_id' component={Ticket} />
        <Route path='/faq' component={FAQ} />
        <Route path='/profile' component={ProfileHome} />
        <Redirect to='/entity-screening' />
      </Switch>
    );
  }

  return (
    <Switch>
      {/* HOMEPAGE */}
      <Route exact path='/' component={NewHomeDashboardPage} />

      {/* REPORTS */}
      <Route path='/report-builder' component={AIReportBuilder} />
      {/* INDUSTRY UPDATES */}
      <Route path='/industry-updates' component={IndustryUpdates} />

      {/* HELP CENTER */}
      <Route path='/faq' component={FAQ} />

      {/* DATA MODULES */}
      <Route path='/data' component={DataPage} />
      <Route path='/watchlist' component={Watchlist} />
      <Route path='/bite-list' component={BiteList} />
      <Route path='/bite-score' component={BiteScore} />
      <Route path='/bite-bulk' component={BiteBulk} />
      <Route path='/bite-trade-protection' component={TradeProtection} />
      <Route path='/bite-cyber-protection' component={CyberProtection} />

      {/* SCREENING MODULES */}
      <Route path='/ace-analyzers/:runId' component={AceRunDetailsPage} />
      <Route path='/tariff-analyzers/:runId' component={TariffRunDetailsPage} />
      <Route path='/aes-analyzers/:runId' component={AesRunDetailsPage} />
      <Route path='/ace-analyzers' component={AceAnalyzerPage} />
      <Route path='/tariff-analyzers' component={TariffAnalyzerPage} />
      <Route path='/aes-analyzers' component={AesAnalyzerPage} />
      <Route path='/customs-entry-analyzer' component={CustomsEntryAnalyzer} />

      <Route path='/export-hub' component={ExportHub} />
      <Route path='/import-hub' component={ImportHub} />
      <Route path='/bite-ai' component={BiteAIStream} />
      <Route path='/bite-viz' component={BiteViz} />
      <Route exact path='/bite-db/products' component={Products} />
      <Route exact path='/bite-db/product/edit' component={EditProduct} />
      <Route exact path='/bite-db/entities' component={EntityDB} />
      <Route
        path='/supplier/:id/verification'
        component={EntityVerificationReport}
      />
      <Route path='/supplier/:id' component={Supplier} />
      <Route path='/product/:id' component={Product} />
      <Route path='/tariff/:id' component={Tariff} />
      <Route path='/database/:id' component={Database} />
      <Route path='/review-queue' component={ReviewQ} />
      <Route path='/stats-roll-up' component={StatsRoll} />
      <Route path='/screening-setting' component={ScreeningPage} />
      <Route path='/entity-screening' component={EntityScreening} />
      <Route path='/alerts-screening' component={AlertsScreening} />
      <Route path='/import-screening' component={ImportScreening} />
      <Route path='/export-screening' component={ExportScreening} />

      {/* Classify */}
      <Route path='/bite-classify' component={Classification} />
      <Route path='/bite-eccn' component={ECCNClassification} />
      <Route path='/bite-ear-classify' component={EARClassification} />

      {/* Tariff */}
      <Route path='/tariff-checker' component={TariffPage} />

      {/* LEADERSHIP */}
      <Route path='/leadership-overview' component={LeadershipOverview} />

      {/* ACCOUNT */}
      <Route path='/profile' component={ProfileHome} />
      <Route path='/join-team/:id' component={JoinTeamFromInvite} />
      <Route path='/_sa/9fc19p3a2/*' component={SuperAdminDashboard} />
      <Route path='/_ta/a83f2d94y' component={TeamAccess} />
      <Route path='/_mt/7b4e91m7x' component={ManualTeamSetup} />
      <Route path='/_ru/5tg8ov34h' component={AdminCreateUser} />
      <Route path='/_tv/4f7d2b4nj/:id' component={TeamSelectView} />

      {/* MONITOR */}
      <Route path='/analytics-vault/case/:case_id' component={Case} />
      <Route path='/analytics-vault/case' component={Case} />
      <Route path='/analytics-vault' component={VaultHome} />
      <Route path='/monitor/ticket/:ticket_id' component={Ticket} />
      <Route path='/bite-monitor' component={Monitor} />

      {/* BITE Workflow */}
      <Route path='/import-page' component={ImportPage} />
      <Route path='/export-page' component={ExportPage} />
      <Route path='/export-design' component={ExportDesignPage} />
      <Route
        path='/beneficial-ownership-workflow'
        component={BeneficialOwnershipPage}
      />

      {/* TRAINING VIDEOS */}
      <Route path='/training-videos' component={TrainingVideos} />
      <Route path='/screening-video' component={ScreenVideo} />
      <Route path='/data-modules-video' component={ModulesVideo} />
      <Route path='/licenses-video' component={LicensesVideo} />
      <Route path='/module-quick-start-video' component={ModuleVideo} />
      <Route path='/payments-video' component={PaymentsVideo} />
      <Route path='/quick-start-video' component={QuickStartVideo} />
      <Route path='/analytics-vault-video' component={VaultVideo} />
      <Route path='/bite-bulk-video' component={BulkVideo} />
      <Route path='/bite-u-video' component={BITEUVideo} />
      <Route path='/bite-gpt-video' component={GPTVideo} />
    </Switch>
  );
};

export default Routes;
